import React from "react";
import styled from "styled-components";
import { useSiteConfig } from "components/Site/Contexts/SiteConfig";
import { StateBible } from "components/Bible/BibleContainer";
import { DownloadButton } from "components/UI/DownloadButton/DownloadButton";
import { bibleDownloader } from "storage/BibleDownloader/BibleDownloader";
import { useBibleDownloads } from "storage/BibleDownloader/BibleDownloadContext";
import { useScriptureRouting } from "components/Bible/useScriptureRouting";
import { useNetworkStatus } from "utils/hooks";
import { isCordovaApp } from "utils/cordovaUtils";
import HeadphonesIcon from "icons/HeadphonesIcon";

interface ListItemProps {
  active?: boolean;
  theme?: any;
  selected?: boolean;
  disabled?: boolean;
}

export const ListItem = styled.div<ListItemProps & JSX.IntrinsicAttributes>`
  box-sizing: border-box;
  padding: 12px 24px;
  width: 100%;
  transition: background 200ms ease;
  display: flex;

  ${({ disabled }) => `
    opacity: ${disabled ? 0.5 : 1};
    cursor: ${disabled ? "not-allowed" : "pointer"};
  `}

  ${({ active, theme }) => {
    const activeColor = theme.darkMode ? theme.appBarText : theme.primary;
    return `
      color: ${theme.primaryText};
      ${active &&
        (theme.scriptDirection === "ltr"
          ? `border-left: 5px solid ${activeColor}; padding-left: 19px;`
          : `border-right: 5px solid ${activeColor}; padding-right: 19px;`)}
    `;
  }};

  &:hover {
    background-color: ${({ theme }) =>
      theme.darkMode ? "#535353" : "#F2F2F2"};
  }
`;

/* Bible List Item Start */
const ListItemIconContainer = styled.div`
  width: 20px;
  flex-shrink: 0;
`;

const AudioIconContainer = styled(ListItemIconContainer)`
  margin-${props =>
    props.theme.scriptDirection === "ltr" ? "right" : "left"}: 8px;
`;

const DownloadIconContainer = styled(ListItemIconContainer)`
margin-${props =>
  props.theme.scriptDirection === "ltr" ? "left" : "right"}: 8px;
`;

const BibleDescription = styled.span<{ theme: any }>`
  color: ${({ theme }) => (theme.darkMode ? "rgba(255,255,255,.8)" : "#666")};
  font-size: 14px;
`;

interface BibleListItemProps extends ListItemProps {
  bible: StateBible;
  onClick: () => void;
  style?: any;
}

export const BibleListItem: React.FC<BibleListItemProps> = ({
  bible,
  selected,
  onClick,
  ...props
}) => {
  const { bibleId: currentBibleId } = useScriptureRouting();
  const { showBibleAbbreviations } = useSiteConfig();
  const { downloads, handleDownload, handleDelete } = useBibleDownloads();
  const downloadState = bibleDownloader.getDownloadState(
    bible.id,
    downloads[bible.id]?.progress || undefined
  );
  const isOnline = useNetworkStatus();
  const isDownloaded = downloads[bible.id]?.progress === 100;
  const isCurrentBible = currentBibleId === bible.id;
  const disabled = !(
    isCordovaApp ||
    isOnline ||
    isDownloaded ||
    isCurrentBible
  );
  const disableOffline = bible.meta?.disableOffline;

  return (
    <ListItem
      selected={selected}
      disabled={disabled}
      onClick={() => {
        !disabled && onClick();
      }}
      {...props}
    >
      <AudioIconContainer>
        {bible.audioBibles && bible.audioBibles.length > 0 && (
          <HeadphonesIcon size={20} />
        )}
      </AudioIconContainer>
      <div style={{ width: "100%" }}>
        {showBibleAbbreviations ? (
          <>
            {bible.abbreviationLocal}
            <br />
            <BibleDescription>{bible.nameLocal}</BibleDescription>
          </>
        ) : (
          bible.nameLocal
        )}
      </div>
      {!isCordovaApp && !disableOffline && (
        <DownloadIconContainer>
          {downloads[bible.id] && (
            <DownloadButton
              disabled={disabled}
              downloadState={downloadState}
              onAbort={downloads[bible.id].abort}
              progress={downloads[bible.id].progress}
              onDelete={handleDelete(bible.id)}
              onDownload={handleDownload(bible.id)}
              offline={!isOnline}
              size={20}
            />
          )}
        </DownloadIconContainer>
      )}
    </ListItem>
  );
};
/* Bible List Item End */

export const ChapterListItem = styled.div<{ active: boolean; theme: any }>`
  display: inline-flex;
  width: 40px;
  height: 40px;
  margin-right: 10px;
  margin-bottom: 10px;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-radius: 2px;

  &:nth-child(6n) {
    margin-right: 0;
  }

  ${({ active, theme }) => {
    const activeColor = theme.darkMode ? theme.appBarText : theme.primary;
    const color = active ? activeColor : theme.appBarText;
    const bgColor = theme.darkMode ? "#535353" : "#F2F2F2";

    return `
    color: ${color};
    ${active && `background-color: ${bgColor};`}
    & > svg { fill: ${color}; }

    &:hover {
      color: ${activeColor};
      background-color: ${bgColor};
      & > svg { fill: ${activeColor}; }
    }
    `;
  }};
`;
